import { useCallback, useEffect, useRef } from 'react';

// Only the newest request may put its answer on screen.
//
// Most pages load data with `useEffect(() => { api.get(...).then(setX) }, [something])`. When
// `something` changes again before the first answer arrives (the next letter typed, another
// station picked, a tap from one detail page to the next, a poll that overtakes a slow one),
// both answers are in flight, and whichever arrives LAST wins. On a slow connection that is often
// the older one, so the page shows the previous station's trains, or the previous shop under the
// new shop's URL, and nothing ever corrects it. 49 of 52 data-loading effects had no guard.
//
//   const latest = useLatestRequest();
//   const load = useCallback(async () => {
//     const req = latest();                                  // supersedes the previous one
//     try {
//       const res = await api.get('/x', { signal: req.signal });
//       if (!req.current()) return;                          // a newer request exists: drop this
//       setX(res.data);
//     } catch {
//       if (!req.current()) return;                          // includes our own abort: not an error
//       setError(true);
//     } finally {
//       if (req.current()) setLoading(false);
//     }
//   }, [latest]);
//
// Starting a request aborts the one before it, so the network does not carry answers nobody will
// read, and unmounting aborts whatever is left. `current()` is the check that matters: an abort
// can lose the race with a response that has already arrived, so every setState must still ask.
// A superseded request always ends in the catch or returns early; it must never set an error,
// clear a spinner, or touch state in any other way.
export default function useLatestRequest() {
  const seq = useRef(0);
  const ctrl = useRef(null);

  useEffect(() => () => {
    seq.current += 1;
    if (ctrl.current) ctrl.current.abort();
  }, []);

  return useCallback(() => {
    if (ctrl.current) ctrl.current.abort();
    const c = new AbortController();
    ctrl.current = c;
    const mine = ++seq.current;
    return { signal: c.signal, current: () => mine === seq.current && !c.signal.aborted };
  }, []);
}
